import { Box, Center, Flex } from "@chakra-ui/react";
import { useMemo } from "react";
import { IScales } from "types/IForecastMap";

interface ColorMapLegendProps {
  colorMap: {
    value: number;
    color: number[] | string;
    textColor?: string;
  }[];
  displayValues?: number[];
  unit: string;
  width?: number;
  humanizedScale?: {
    skip: number;
    scales: IScales[];
  };
}

const getBackgroundColor = (color: number[] | string) =>
  Array.isArray(color) ? `rgb(${color.join(", ")})` : color;

const ColorMapLegend: React.FC<ColorMapLegendProps> = ({
  unit,
  colorMap,
  width,
  humanizedScale,
}) => {
  const sortedColorMap = useMemo(
    () => [...colorMap].sort((a, b) => a.value - b.value),
    [colorMap],
  );

  if (!sortedColorMap?.length) {
    return null;
  }

  // The empty space in the concepts row must span the unit cell (flex=1)
  // plus `skip` colormap items (each flex=1), so flex = 1 + skip.
  const emptySpaceFlex = 1 + (humanizedScale?.skip ?? 0);

  return (
    <Box>
      {humanizedScale?.scales && (
        <Flex width={width} borderRadius={4} mr={"6px"} height="20px">
          <Box flex={emptySpaceFlex} />
          {humanizedScale.scales.map((concept, idx) => {
            const isLast = idx === humanizedScale.scales.length - 1;

            return (
              <Box
                key={idx}
                flex={concept.length}
                textAlign="center"
                fontSize="xs"
                fontWeight="light"
                color="white"
                border={"1px solid rgb(255, 255, 255)"}
                borderBottom="0px"
                borderRight={isLast ? "1px solid white" : "0px"}
                bg={"rgba(0, 0, 0, 0.33)"}
                overflow="hidden"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
              >
                {concept.label}
              </Box>
            );
          })}
        </Flex>
      )}
      <Box width={width} borderRadius={4} mr={"6px"} height="20px">
        <Flex height="100%">
          <Center
            flex={1}
            bg={getBackgroundColor(sortedColorMap[0].color)}
            color={sortedColorMap[0]?.textColor ?? "white"}
            borderLeftRadius={4}
          >
            {unit}
          </Center>
          {sortedColorMap.map((item, index) => (
            <Center
              key={item.value}
              flex={1}
              bg={getBackgroundColor(item.color)}
              color={item.textColor ?? "white"}
              borderRightRadius={sortedColorMap.length - 1 === index ? 4 : 0}
              fontSize="12px"
            >
              {item.value}
            </Center>
          ))}
        </Flex>
      </Box>
    </Box>
  );
};

export default ColorMapLegend;
